import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ExternalLink, RefreshCw, X } from 'lucide-react'
import cdxLogo from '../assets/cdx.png'

/**
 * Modal da assinatura CDX — lançamentos em aberto e pagos, 3 por página.
 *
 * SEMPRE por createPortal no document.body: o card vive no <header>, que tem
 * `backdrop-blur-md`, e backdrop-filter cria containing block — um overlay
 * `fixed` renderizado lá dentro se ancora na caixa do header (56/64px) em vez
 * da janela. Foi exatamente isso que travou o modal em outro sistema da CDX.
 *
 * `modoAviso` é o alerta de risco de suspensão — regra geral da CDX, mostrado
 * uma vez por sessão quando há parcela vencida.
 */

/** Lançamentos por página, nas DUAS abas. Regra fixada pelo Ricardo. */
const POR_PAGINA = 3

const moeda = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const dataBr = (iso) => {
  if (!iso) return '--'
  const [a, m, d] = String(iso).slice(0, 10).split('-')
  return a && m && d ? `${d}/${m}/${a}` : '--'
}

/* barra colorida à esquerda da linha (regra canônica de cor por situação) */
const BARRA = {
  em_dia: 'border-l-emerald-500',
  a_vencer: 'border-l-amber-500',
  vencido: 'border-l-red-500',
  pago: 'border-l-emerald-500',
}

const SELO = {
  em_dia: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  a_vencer: 'bg-amber-50 text-amber-700 border-amber-200',
  vencido: 'bg-red-50 text-red-700 border-red-200',
  pago: 'bg-emerald-50 text-emerald-700 border-emerald-200',
}

const ROTULO_SELO = { em_dia: 'Em dia', a_vencer: 'A vencer', vencido: 'Vencida', pago: 'Paga' }

function Linha({ titulo, diasLimite }) {
  const sit = titulo.situacao || 'em_dia'
  const pago = sit === 'pago'
  return (
    <div className={`flex items-center gap-3 rounded-xl border border-gray-100 border-l-4 ${BARRA[sit]} bg-white px-3 py-2.5`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-graphite" title={titulo.descricao}>
          {titulo.descricao || 'Lançamento'}
        </p>
        <p className="mt-0.5 text-[11px] text-gray-500">
          {pago
            ? `Pago em ${dataBr(titulo.dataPagamento || titulo.dataVencimento)}`
            : `Vencimento ${dataBr(titulo.dataVencimento)}`}
          {!pago && titulo.vencido ? ` · ${titulo.diasAtraso} dia${titulo.diasAtraso > 1 ? 's' : ''} em atraso` : ''}
        </p>
      </div>

      <span className={`flex-shrink-0 rounded-lg border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${SELO[sit]}`}>
        {ROTULO_SELO[sit]}
      </span>

      <span className="flex-shrink-0 text-sm font-extrabold text-graphite">
        {/* `||` e não `??` para casar com o backend (totalPago usa `or`): um pago
            com valorRecebido=0 mostraria R$ 0,00 na linha e o valor cheio no total. */}
        {moeda(pago ? titulo.valorRecebido || titulo.valor : titulo.valor)}
      </span>

      {/* Cadeia EXAUSTIVA: todo título em aberto mostra exatamente um estado.
          Sem o caso do meio, parcela dentro do prazo mas ainda sem boleto emitido
          (invoiceUrl nulo no CDX Adm) ficava sem botão e sem explicação. */}
      {!pago &&
        (titulo.podePagar ? (
          titulo.linkPagamento ? (
            <a
              href={titulo.linkPagamento}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-accent-500 px-2.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-accent-600"
            >
              <ExternalLink size={13} /> Pagar
            </a>
          ) : (
            <span className="flex-shrink-0 text-[11px] font-semibold text-gray-500"
                  title="O boleto ainda não foi emitido para esta parcela. Fale com a CDX.">
              Boleto a emitir
            </span>
          )
        ) : (
          <span className="flex-shrink-0 text-[11px] font-semibold text-gray-500"
                title={`O prazo de ${diasLimite} dias para pagamento desta parcela expirou. Fale com a CDX.`}>
            Prazo expirado
          </span>
        ))}
    </div>
  )
}

export default function AssinaturaCdxModal({ resumo, modoAviso, onFechar, onAtualizar, atualizando }) {
  const [aba, setAba] = useState('aberto')
  const [pagina, setPagina] = useState(1)

  useEffect(() => {
    const porTecla = (e) => { if (e.key === 'Escape') onFechar() }
    document.addEventListener('keydown', porTecla)
    const antes = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', porTecla)
      document.body.style.overflow = antes
    }
  }, [onFechar])

  useEffect(() => { setPagina(1) }, [aba])

  const emAberto = resumo.emAberto || []
  const pagos = resumo.pagos || []
  const itens = aba === 'aberto' ? emAberto : pagos
  const paginas = Math.max(1, Math.ceil(itens.length / POR_PAGINA))
  const atual = Math.min(pagina, paginas)
  const visiveis = itens.slice((atual - 1) * POR_PAGINA, atual * POR_PAGINA)
  const vencidas = emAberto.filter((t) => t.vencido)
  const contrato = resumo.contrato

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-black/50 p-4"
         onClick={onFechar} role="dialog" aria-modal="true" aria-label="Assinatura CDX">
      <div className="my-8 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl tc-pop"
           onClick={(e) => e.stopPropagation()}>
        {/* cabeçalho */}
        <div className="flex items-center gap-3 bg-gradient-to-r from-primary-50 to-accent-50 px-4 py-3 sm:px-5 tc-hcard">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white p-1 border border-accent-200">
            <img src={cdxLogo} alt="CDX" className="h-full w-full object-contain" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-extrabold leading-tight text-graphite sm:text-base">
              Serviço Cloud CDX VIP
            </p>
            <p className="mt-0.5 truncate text-[11px] text-gray-600">
              {contrato?.descricao || 'Assinatura mensal CDX Sistemas'}
              {contrato?.diaVencimento ? ` · vence todo dia ${contrato.diaVencimento}` : ''}
              {contrato?.valorMensal ? ` · ${moeda(contrato.valorMensal)}/mês` : ''}
            </p>
          </div>
          <button type="button" onClick={onAtualizar} disabled={atualizando}
                  title="Buscar a situação agora, ignorando o cache"
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-accent-300 bg-white/70 px-2.5 py-1.5 text-xs font-bold text-accent-700 transition-colors hover:border-accent-500 disabled:opacity-60">
            <RefreshCw size={13} className={atualizando ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{atualizando ? 'Atualizando' : 'Atualizar'}</span>
          </button>
          <button type="button" onClick={onFechar} aria-label="Fechar" title="Fechar"
                  className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white/70 text-gray-600 transition-colors hover:border-gray-400">
            <X size={15} />
          </button>
        </div>

        {/* aviso de suspensão */}
        {(modoAviso || vencidas.length > 0) && (
          <div className="mx-4 mt-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] leading-relaxed text-red-800 sm:mx-5">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <p>
              {vencidas.length > 0 ? (
                <>
                  <strong className="font-extrabold">
                    {vencidas.length === 1 ? 'Há 1 mensalidade em atraso.' : `Há ${vencidas.length} mensalidades em atraso.`}
                  </strong>{' '}
                  O serviço poderá sofrer suspensão a qualquer momento enquanto houver atraso. O
                  pagamento pode ser feito em até {resumo.diasLimitePagamento} dias do vencimento;
                  depois disso o boleto expira e é preciso falar com a CDX.
                </>
              ) : (
                'O serviço poderá sofrer suspensão a qualquer momento em caso de atraso.'
              )}
            </p>
          </div>
        )}

        {/* resumo */}
        <div className="flex flex-wrap gap-2.5 px-4 pt-4 sm:px-5">
          {[
            { r: 'Situação', v: resumo.rotulo },
            { r: 'Em aberto', v: moeda(resumo.totalEmAberto) },
            { r: 'Total pago', v: moeda(resumo.totalPago) },
          ].map((c) => (
            <div key={c.r} className="min-w-[130px] flex-1 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{c.r}</p>
              <p className="mt-0.5 text-sm font-extrabold text-graphite">{c.v}</p>
            </div>
          ))}
        </div>

        {/* abas */}
        <div className="mt-4 flex gap-1 border-b border-gray-100 px-4 sm:px-5">
          {['aberto', 'pagos'].map((k) => (
            <button key={k} type="button" onClick={() => setAba(k)}
                    className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${
                      aba === k ? 'border-accent-500 text-accent-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
              {k === 'aberto' ? `Em aberto (${emAberto.length})` : `Pagos (${pagos.length})`}
            </button>
          ))}
        </div>

        {/* lista */}
        {visiveis.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-gray-400">
            {aba === 'aberto' ? 'Nenhuma mensalidade em aberto.' : 'Nenhum pagamento registrado.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2 px-4 pt-3 sm:px-5">
            {visiveis.map((t) => (
              <Linha key={t.id || t.dataVencimento} titulo={t} diasLimite={resumo.diasLimitePagamento} />
            ))}
          </div>
        )}

        {paginas > 1 && (
          <div className="flex items-center justify-center gap-3 px-5 pt-3 text-xs text-gray-600">
            <button type="button" onClick={() => setPagina(atual - 1)} disabled={atual <= 1}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 font-bold disabled:opacity-45">
              Anterior
            </button>
            <span>Página {atual} de {paginas}</span>
            <button type="button" onClick={() => setPagina(atual + 1)} disabled={atual >= paginas}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 font-bold disabled:opacity-45">
              Próxima
            </button>
          </div>
        )}

        {/* rodapé */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
          <span className="text-[11px] text-gray-500">
            {resumo.atualizadoEm
              ? `Atualizado em ${dataBr(resumo.atualizadoEm)} às ${String(resumo.atualizadoEm).slice(11, 16)}`
              : ''}
          </span>
          <button type="button" onClick={onFechar}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:border-gray-400">
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
